import { AlertTriangle, Clock, Stethoscope, Users } from 'lucide-react';
import {
  PageHeader, KpiCard, Card, SegmentedControl, FilterChip, SearchInput,
  Skeleton, SkeletonRegion, SkeletonRow, SkeletonInput, SkeletonButton,
} from '@parsina/framework';
import { C, soft } from '@parsina/framework/tokens';
import { useTranslation } from '../../i18n';
import { STACK } from '../../constants/density';
import useResponsive from '../../hooks/useResponsive';
import {
  MobileFilterBar, MobileHeader, MobileList, MobileScreen, MobileSectionNav, MobileStatTiles,
  MobileCardSkeleton,
} from '../../mobile';

/**
 * «همکاران» while it loads — the page's own shape in placeholder blocks
 * (DESIGN.md §"Loading & skeleton").
 *
 * This file is the router's Suspense fallback for `/people`, so it is imported
 * eagerly and stays light: kit primitives, tokens, density, i18n and the mobile
 * layout library only — never the screen, its hooks or its data. The screen
 * itself renders the SAME body on its first load (`PeopleSkeletonWebBody`,
 * `renderPersonSkeleton`, …) so the fallback → first-load → data transitions
 * draw one shape three times rather than three shapes.
 *
 * Static chrome — the title, the tab labels, the kind chips, the KPI labels,
 * the column headers — renders for real; anything that needs data (counts,
 * the role filter's options, the rows) is a block.
 */

const noop = () => {};

/** The people table's column headers, in the order the screen draws them. */
function useColumns(t, actions) {
  return [
    { key: 'name', header: t('people.fields.person'), width: 160 },
    { key: 'kind', header: t('common.fields.type'), width: 72 },
    { key: 'roles', header: t('people.fields.roleOrProfession'), width: 110 },
    { key: 'status', header: t('common.fields.status'), width: 96 },
    { key: 'joinedAt', header: t('people.fields.membership'), align: 'end', width: 80 },
    ...(actions ? [{ key: 'actions', header: t('people.fields.action'), align: 'end', width: 120 }] : []),
  ];
}

/**
 * The people table with real headers and placeholder rows. The screen uses it
 * for a refetch too, so the header row is the one the data will land under.
 * `columns` — `[{ key, header, align? }]`, the screen's own column list.
 */
export function PeopleTableSkeleton({ columns, rows = 5, widths, style }) {
  const cellWidths = widths || columns.map((c) => c.width || 90);
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', ...style }}>
      {/* A real <table>: SkeletonRow renders a <tr>, which a <div> may not
          contain — and the placeholder has to carry the real headers and
          column count anyway, or the page jumps when the data lands. */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84375rem' }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={{
                background: C.bg, color: C.textSecondary, fontWeight: 700, fontSize: '0.78125rem',
                textAlign: col.align === 'end' ? 'left' : 'right', padding: '10px 14px',
                borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
              }}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <SkeletonRow key={i} cols={columns.length} widths={cellWidths} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One role card's footprint — name, member/permission count, template line, the edit arrow. */
export function RoleCardSkeleton() {
  return (
    <Card aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 118 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Skeleton width="45%" height={18} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Skeleton width="60%" height={14} />
        <Skeleton width="50%" height={12} />
      </div>
      <Skeleton width={64} height={14} />
    </Card>
  );
}

/** The roles grid in placeholder — the RolesTab body. */
export function RoleCardsSkeleton({ count = 3, label, style }) {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={label ?? t('common.state.loading')}
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, ...style }}>
      {Array.from({ length: count }, (_, i) => <RoleCardSkeleton key={i} />)}
    </SkeletonRegion>
  );
}

/**
 * The desktop page BODY (everything under the PageHeader), so the screen can
 * put it inside its own shell — the one that carries the real action button.
 */
export function PeopleSkeletonWebBody({ tab = 'people', actions = true, staff = true, practitioners = true }) {
  const { t } = useTranslation();
  const columns = useColumns(t, actions);
  const tabOptions = [
    { value: 'people', label: t('people.tabs.people') },
    { value: 'roles', label: t('people.fields.roles') },
  ];

  return (
    <SkeletonRegion label={t('common.state.loading')} data-testid="people-skeleton-body">
      {/* zone ③ — KPI strip: the labels are static, the numbers are the kit's loading dash */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        {practitioners && (
          <KpiCard label={t('people.kpi.practitioners')} value="" loading
            icon={<Stethoscope size={20} color={C.teal} />} bg={C.tealLight} />
        )}
        {staff && (
          <KpiCard label={t('people.kpi.staff')} value="" loading
            icon={<Users size={20} color={C.navy} />} bg={soft(C.navy, 0.06)} />
        )}
        <KpiCard label={t('people.kpi.pending')} value="" loading
          icon={<Clock size={20} color={C.amber} />} bg={C.amberLight} />
        <KpiCard label={t('people.kpi.needsAttention')} value="" loading
          icon={<AlertTriangle size={20} color={C.amber} />} bg={C.amberLight} />
      </div>

      {/* zone ④ — toolbar: real chrome where it needs no data, blocks where it does */}
      <div style={{ paddingBlock: STACK.tight, marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {staff && <SegmentedControl value={tab} onChange={noop} options={tabOptions} />}
          {tab === 'people' ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <FilterChip label={t('people.tabs.all')} active onClick={noop} />
              {practitioners && <FilterChip label={t('people.kinds.practitionerPlural')} onClick={noop} />}
              {staff && <FilterChip label={t('people.kinds.staffPlural')} onClick={noop} />}
            </div>
          ) : (
            <Skeleton width={72} height={13} style={{ marginInlineStart: 'auto' }} />
          )}
        </div>

        {tab === 'people' && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <SearchInput value="" onChange={noop} placeholder={t('people.search.placeholder')} readOnly />
            </div>
            {staff && <SkeletonInput label={false} width={168} />}
            <SkeletonInput label={false} width={168} />
            <SkeletonInput label={false} width={168} />
          </div>
        )}
      </div>

      {/* zone ⑤ — content */}
      {tab === 'people'
        ? <PeopleTableSkeleton columns={columns} />
        : <RoleCardsSkeleton />}
    </SkeletonRegion>
  );
}

/** The whole desktop page: the real PageHeader over the body. */
export function PeopleSkeletonWeb(props) {
  const { t } = useTranslation();
  return (
    <div data-state="loading" data-testid="people-skeleton"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, position: 'relative' }}>
      <PageHeader title={t('people.title')} sub={t('people.subtitle')}>
        <SkeletonButton width={132} />
      </PageHeader>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 32px' }}>
        <PeopleSkeletonWebBody {...props} />
      </div>
    </div>
  );
}

/**
 * One `MobilePersonCard`'s placeholder: a 40px avatar, the name with a status
 * pill trailing, the kind badge + role line under it.
 */
export const renderPersonSkeleton = () => <MobileCardSkeleton avatar={40} badge={56} metaLines={1} chips={1} />;

/** One `MobileRoleCard`'s placeholder: name, the count line, the template line, a chevron. */
export const renderRoleSkeleton = () => <MobileCardSkeleton avatar={0} badge={18} metaLines={2} />;

/** The mobile page: the real header band, the section strip, the tiles, and card placeholders. */
export function PeopleSkeletonMobile({ tab = 'people', staff = true, practitioners = true }) {
  const { t } = useTranslation();

  const tiles = [
    practitioners && { key: 'practitioners', label: t('people.kpi.practitioners'), icon: <Stethoscope size={16} aria-hidden="true" />, color: C.teal, solid: true },
    staff && { key: 'staff', label: t('people.kpi.staff'), icon: <Users size={16} aria-hidden="true" />, color: C.navy },
    { key: 'pending', label: t('people.kpi.pending'), icon: <Clock size={16} aria-hidden="true" />, color: C.amber },
    { key: 'needsAttention', label: t('people.kpi.needsAttention'), icon: <AlertTriangle size={16} aria-hidden="true" />, color: C.amber },
  ].filter(Boolean);

  const header = (
    <MobileHeader
      title={t('people.title')}
      subtitle={t('people.subtitle')}
      onBack={() => window.history.back()}
      backLabel={t('common.actions.back')}
      data-testid="people-header"
    />
  );

  return (
    <MobileScreen header={header} data-state="loading" data-testid="people-skeleton">
      {staff && (
        <MobileSectionNav
          items={[
            { key: 'people', label: t('people.tabs.people') },
            { key: 'roles', label: t('people.fields.roles') },
          ]}
          active={tab}
          onSelect={noop}
          ariaLabel={t('people.title')}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: `${STACK.regular}px`, paddingTop: '0.75rem' }}>
        {tab === 'people' ? (
          <>
            <MobileStatTiles tiles={tiles} loading columns={tiles.length} />
            <MobileFilterBar
              sticky={false}
              chips={[
                { id: 'all', label: t('people.tabs.all'), active: true },
                ...(practitioners ? [{ id: 'practitioner', label: t('people.kinds.practitionerPlural') }] : []),
                ...(staff ? [{ id: 'staff', label: t('people.kinds.staffPlural') }] : []),
              ]}
              onSelect={noop}
            />
            <div style={{ display: 'grid', gap: `${STACK.tight}px`, paddingInline: '1rem' }}>
              <SearchInput value="" onChange={noop} placeholder={t('people.search.placeholder')} readOnly />
            </div>
            <div style={{ paddingInline: '1rem' }}>
              <MobileList loading items={[]} renderItem={noop} renderSkeleton={renderPersonSkeleton} />
            </div>
          </>
        ) : (
          <div style={{ display: 'grid', gap: `${STACK.regular}px`, paddingInline: '1rem' }}>
            <MobileList loading items={[]} renderItem={noop} skeletonCount={3} renderSkeleton={renderRoleSkeleton} />
          </div>
        )}
      </div>
    </MobileScreen>
  );
}

export default function PeopleSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <PeopleSkeletonMobile {...props} /> : <PeopleSkeletonWeb {...props} />;
}
